import { useEffect, useRef } from "react"
import { Alert, Box, Button, Divider, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material"
import { BoundAutocomplete, BoundTextField } from "lib/@components/binding/bound-components"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { If } from "lib/@components/switch"
import { isEnabled } from "lib/@components/feature"
import Iconify from "minimals-template/components/Iconify"
import { skillMaps } from "routes/skill-mapper/controller/skill-maps"
import { skillMapLines } from "routes/skill-mapper/controller/skill-map-lines"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { getCurrencySymbol } from "../format-utils"
import { useUniqueNameValidator } from "../use-unique-name-validation"
import { useCreateSkillMap } from "../skill-mappings/use-create-skill-map"
import { FEATURE_FLAG_SCHEDULE_ASSIGNMENTS } from "../../feature-flag-schedule-assignments"

export function CreateRateCardFormBody({ regimeId }) {
    const { target, refresh } = useBoundContext()
    const handleCreateSkillMap = useCreateSkillMap({ navigateOnCreate: false, regimeId: target.regimeId || regimeId })
    const result = skillMaps.useResults({ skip: 0, take: 1000, status: "active" })
    // status:"all" so the duplicate-name check matches the server (which counts archived too).
    const rcResult = rateCards.useResults({ skip: 0, take: 1000, status: "all" })
    const validateName = useUniqueNameValidator(rcResult?.items, "rate card")
    const hasSetDefault = useRef(false)
    useEffect(prePopulateName, [rcResult])
    const existingMaps = (result?.items || []).map(toSkillMapOption)

    const selectedMapId = target?.skillMapId?.value ?? target?.skillMapId
    const selectedMap = selectedMapId ? (result?.items || []).find(isSelectedMap) : null
    const totalTrades = selectedMap ? Object.keys(selectedMap.trades || {}).length : 0

    const lines = skillMapLines.useResults(selectedMapId ? { skillMapId: selectedMapId } : null)
    const mappedTradeIds = lines ? new Set(lines.filter(hasTradeId).map(toTradeId)) : new Set()
    // FIQ-2457: with schedule assignments on, every trade gets a line (it may only receive
    // assigned work), so no trade is left off and there is nothing to warn about
    const hasUnmappedTrades =
        !isEnabled(FEATURE_FLAG_SCHEDULE_ASSIGNMENTS) &&
        selectedMap &&
        totalTrades > 0 &&
        mappedTradeIds.size < totalTrades

    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            <BoundTextField field="name" label="Rate Card Name" yup={validateName} placeholder="Enter a name..." />
            <BoundAutocomplete
                field="skillMapId"
                label="Skill Mapping"
                options={existingMaps}
                sideEffects={true}
                yup="mixed.required"
                noOptionsText={renderSkillMapEmptyState()}
            />
            <BoundTextField
                field="description"
                label="Description"
                placeholder="Optional description..."
                multiline
                minRows={2}
            />
            <If value={hasUnmappedTrades}>
                <Alert severity="info" data-cy="unmapped-skills-note">
                    The selected skill map has {totalTrades - mappedTradeIds.size} trade
                    {totalTrades - mappedTradeIds.size === 1 ? "" : "s"} without mapped skills. Only mapped trades will
                    appear on the rate card.
                </Alert>
            </If>
            <Divider />
            <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        select
                        label="Currency"
                        value={target.currency || "GBP"}
                        onChange={handleCurrencyChange}
                        fullWidth
                    >
                        <MenuItem value="GBP">GBP (£)</MenuItem>
                        <MenuItem value="USD">USD ($)</MenuItem>
                        <MenuItem value="EUR">EUR (€)</MenuItem>
                    </TextField>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        label="Default Hourly Rate"
                        type="number"
                        value={target.defaultRate ?? ""}
                        onChange={handleDefaultRateChange}
                        onWheel={(e) => e.target.blur()}
                        fullWidth
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    {getCurrencySymbol(target.currency || "GBP")}
                                </InputAdornment>
                            ),
                        }}
                        placeholder="0.00"
                    />
                </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary">
                Setting a default hourly rate will apply it to all trades. You can adjust individual trade rates later.
            </Typography>
        </Stack>
    )

    function prePopulateName() {
        if (hasSetDefault.current || !rcResult || target.name) return
        hasSetDefault.current = true
        const count = rcResult?.totalCount ?? 0
        target.name = `Rate Card ${count + 1}`
        refresh()
    }

    function handleCurrencyChange(e) {
        target.currency = e.target.value
        refresh()
    }

    function handleDefaultRateChange(e) {
        const parsed = parseFloat(e.target.value)
        target.defaultRate = Number.isNaN(parsed) ? null : parsed
        refresh()
    }

    function renderSkillMapEmptyState() {
        return (
            <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
                <Typography variant="body1" sx={{ color: "text.primary" }}>
                    No Skill Mappings added yet
                </Typography>
                <Button
                    data-cy="dropdown-add-skill-map"
                    variant="text"
                    color="primary"
                    startIcon={<Iconify icon="mdi:plus" />}
                    onMouseDown={handleAddSkillMap}
                    sx={{ fontWeight: 600 }}
                >
                    Add Skill Mapping
                </Button>
            </Stack>
        )
    }

    function handleAddSkillMap(event) {
        event.preventDefault()
        event.stopPropagation()
        createAndSelectSkillMap()
    }

    async function createAndSelectSkillMap() {
        const created = await handleCreateSkillMap()
        if (!created?.id) return
        target.skillMapId = { value: created.id, label: created.name }
        refresh()
    }

    function isSelectedMap(m) {
        return m.id === selectedMapId
    }

    function hasTradeId(l) {
        return l.tradeId
    }

    function toTradeId(l) {
        return l.tradeId
    }
}

function toSkillMapOption(map) {
    return { value: map.id, label: map.name }
}
